import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { parseSafetyContour } from '../plugins/utils/helpers';
import {
  LAYERS_PARAMETERS_UPDATED_EVENT_TYPE,
  COLOR_CHANGE_EVENT_TYPE,
  ONLINE_CHANGE_EVENT_TYPE,
  ONLINE_MESSAGE_CHANGE_EVENT_TYPE,
  MAPDBVERSION_MESSAGE_RECEIVED_EVENT_TYPE,
  CLEAR_OL_CACHE_EVENT_TYPE,
  MAP_DATABASE_UPDATED_EVENT_TYPE
} from '../plugins/utils/events.js';
import {
  LIGHT_CONDITION
} from '../plugins/utils/constantes.js'
import { v4 as uuidv4 } from 'uuid'

export const usePortAllData = defineStore('PortAllData', () => {
  const defaultFeatureInfoPosition = {
    left: '4px',
    bottom: '100px',
    height: '300px',
  }

  const defaultAisData = {
    center: null,
    eta: [],
    guardzone: [],
    repeater: null,
    trace: []
  }

  const defaultReplayAisData = {
    eta: [],
    repeater: null,
    trace: []
  }

  function $reset() {
    setAuthConfig(null)
    setOnline(true)
    setMapDbVersion('1')
    setFontProfile(0)
    setVisibleOverview(false)
    resetVisibleLayers()
    setMapView({ long: 0, lat: 0, zoom: 3 })
    setLayerExtraParameters({})
    setMeasureUnit(null)
    setLayerColor(LIGHT_CONDITION.DAY)
    setModuleOpen(false)
    setSimplifyGetFeatureInfo(false)
    setFeatureInfo(defaultFeatureInfoPosition)
    setEditionMode(false)
    setAisData(defaultAisData)
    setReplayAisData(defaultReplayAisData)
    setModuleProcessing({})
    setCenterPositionDisplayed(false)
    setIsMobile(false)
    setDisplayDraughtControl(false)
    setLayerListFilter('ENC')
  }

  // authConfig
  const authConfig = ref(null)

  const getAuthConfig = computed(() => authConfig.value)

  function setAuthConfig(_authConfig) {
    authConfig.value = _authConfig
  }

  // locales
  const locale = ref(localStorage.getItem('locale') || 'en');

  const getLocale = computed(() => locale.value);

  function setLocale(_locale) {
    localStorage.setItem('locale', _locale);
    locale.value = _locale;
  }

  // online
  const online = ref(typeof localStorage.getItem('online') === 'boolean' ? localStorage.getItem('online') : localStorage.getItem('online') === 'true')

  const getOnline = computed(() => online.value)

  function setOnline(_online) {
    localStorage.setItem('online', _online)
    online.value = _online
    const event = new Event(ONLINE_CHANGE_EVENT_TYPE);
    document.dispatchEvent(event);
  }

  document.addEventListener(ONLINE_MESSAGE_CHANGE_EVENT_TYPE, (e) => {
    const oldOnline = online.value
    const newOnline = e.detail.online
    if (oldOnline !== newOnline) setOnline(newOnline)
  })

  // MapDbVersion
  const mapDbVersion = ref(localStorage.getItem('mapDBVersion') || '1')

  const getMapDbVersion = computed(() => mapDbVersion.value)

  function setMapDbVersion(version) {
    localStorage.setItem('mapDBVersion', version)
    mapDbVersion.value = version
  }

  document.addEventListener(MAPDBVERSION_MESSAGE_RECEIVED_EVENT_TYPE, (e) => {
    const oldDBVersion = mapDbVersion.value
    if (oldDBVersion && oldDBVersion !== e.detail) {
      setMapDbVersion(e.detail)
      document.dispatchEvent(new CustomEvent(CLEAR_OL_CACHE_EVENT_TYPE))
      document.dispatchEvent(new CustomEvent(MAP_DATABASE_UPDATED_EVENT_TYPE))
    }
  })

  // font profile
  const fontProfile = ref(localStorage.getItem('fontProfile') || 0);

  const getFontProfile = computed(() => fontProfile.value);

  function setFontProfile(fontSizeMultiplier) {
    localStorage.setItem('fontProfile', fontSizeMultiplier);
  }

  // visible overview
  const visibleOverview = ref(localStorage.getItem('visibleOverview') || false);

  const getVisibleOverview = computed(() => typeof visibleOverview.value === 'boolean' ? visibleOverview.value : visibleOverview.value === 'true');

  function setVisibleOverview(visible) {
    localStorage.setItem('visibleOverview', visible);
    visibleOverview.value = visible;
  }

  // visible layers
  const visibleLayers = ref(JSON.parse(localStorage.getItem('visibleLayers')) || ['background']);

  const getVisibleLayers = computed(() => visibleLayers.value);

  function resetVisibleLayers() {
    localStorage.setItem('visibleLayers', JSON.stringify(['background']));
  }

  function setVisibleLayers(layerName) {
    const vl = visibleLayers.value;
    if (vl.includes(layerName)) {
      const i = vl.indexOf(layerName);
      vl.splice(i, 1);
    } else {
      if (Array.isArray(layerName)) vl.push(...layerName);
      else vl.push(layerName);
    }
    visibleLayers.value = vl;
    localStorage.setItem('visibleLayers', JSON.stringify(vl));
  }

  //mapView
  const mapView = ref(JSON.parse(localStorage.getItem('mapView')) || { long: 0, lat: 0, zoom: 3 });

  const getMapView = computed(() => mapView.value)

  function setMapView(mView) {
    localStorage.setItem('mapView', JSON.stringify(mView));
    mapView.value = mView;
  }

  // layerExtraParameters
  const layerExtraParameters = ref(JSON.parse(localStorage.getItem('layerExtraParameters')) || {});

  const getLayerExtraParameters = computed(() => layerExtraParameters.value);

  function setLayerExtraParameters(ep) {
    const lep = JSON.parse(localStorage.getItem('layerExtraParameters')) || {};
    if (ep.coordinateFormat) delete ep.coordinateFormat;
    const newLep = { ...lep, ...ep };
    localStorage.setItem('layerExtraParameters', JSON.stringify(newLep));
    layerExtraParameters.value = newLep;
  }

  function updateLayerExtraParameters(paramList) {
    const parameters = layerExtraParameters.value || {};
    const updatedParameterList = [];
    for (const param of paramList) {
      parameters[param.paramKey] = param.paramValue;
      updatedParameterList.push(param.paramKey);
    }
    setLayerExtraParameters(parameters);
    const event = new CustomEvent(LAYERS_PARAMETERS_UPDATED_EVENT_TYPE, { detail: { parameters: updatedParameterList } });
    document.dispatchEvent(event);
  }

  function getContours() {
    return parseSafetyContour(layerExtraParameters.value['SAFETY_CONTOUR'])
  }

  function setContours(newValue) {
    let contours;
    if (newValue instanceof Array) {
      contours = newValue;
    } else {
      contours = getContours();
      contours[0] = newValue;
      contours[2] = newValue;
    }
    this.updateLayerExtraParameters([{ paramKey: 'SAFETY_CONTOUR', paramValue: contours }]);
  }

  // measureUnit
  const measureUnit = ref(localStorage.getItem('measureUnit') || null)

  const getMeasureUnit = computed(() => measureUnit.value)

  function setMeasureUnit(_mu) {
    localStorage.setItem('measureUnit', _mu)
    measureUnit.value = _mu
  }

  // layerColor
  const layerColor = ref(localStorage.getItem('layerColor') || LIGHT_CONDITION.DAY);

  const getLayerColor = computed(() => layerColor.value);

  function setLayerColor(color) {
    localStorage.setItem('layerColor', color);
    layerColor.value = color;
    const event = new Event(COLOR_CHANGE_EVENT_TYPE);
    document.dispatchEvent(event);
  }

  // moduleOpen
  const moduleOpen = ref(false);

  const getModuleOpen = computed(() => moduleOpen.value);

  function setModuleOpen(open) {
    moduleOpen.value = open;
  }

  // check if ais module is open
  const moduleAisIsOpen = ref(false)

  const getModuleAisIsOpen = computed(() => moduleAisIsOpen.value);

  function setModuleAisIsOpen(isOpen) {
    moduleAisIsOpen.value = isOpen;
  }

  // check if replay module is open
  const moduleReplayIsOpen = ref(false)
  
  const getModuleReplayIsOpen = computed(() => moduleReplayIsOpen.value)

  function setModuleReplayIsOpen(isOpen) {
    moduleReplayIsOpen.value = isOpen
  }

  // simplifyGetFeatureInfo
  const simplifyGetFeatureInfo = ref(localStorage.getItem('simplifyGetFeatureInfo') || false);

  const getSimplifyGetFeatureInfo = computed(() => typeof simplifyGetFeatureInfo.value === 'boolean' ? simplifyGetFeatureInfo.value : simplifyGetFeatureInfo.value === 'true'); 

  function setSimplifyGetFeatureInfo(isSimplified) {
    localStorage.setItem('simplifyGetFeatureInfo', isSimplified);
    simplifyGetFeatureInfo.value = isSimplified;
    // send(document, SIMPLIFY_GET_FEATURE_INFO_EVENT_TYPE);
  }

  // featureInfo position and height
  const featureInfo = ref(JSON.parse(localStorage.getItem('featureInfo')) || defaultFeatureInfoPosition)

  const getFeatureInfo = computed(() => featureInfo.value)

  function setFeatureInfo(fi) {
    localStorage.setItem('featureInfo', JSON.stringify(fi))
    featureInfo.value = fi
  }

  // editionMode
  const editionMode = ref(false)

  const getEditionMode = computed(() => editionMode.value)
  
  function setEditionMode(value) {
    editionMode.value = value
  }

  // aisData
  const aisData = ref(JSON.parse(localStorage.getItem('ais')) || defaultAisData)

  const getAisData = computed(() => aisData.value)

  function setAisData(key, data) {
    const ais = aisData.value
    ais[key] = data
    localStorage.setItem('ais', JSON.stringify(ais))
    aisData.value = ais
  }

  // aisData for replay
  const replayAisData = ref(defaultReplayAisData)

  const getReplayAisData = computed(() => replayAisData.value)

  function setReplayAisData(key, data) {
    const ais = replayAisData.value
    ais[key] = data
    replayAisData.value = ais
  }

  // moduleProcessing
  const moduleProcessing = ref({})

  const getModuleProcessing = computed(() => moduleProcessing.value)

  function setModuleProcessing(code, isProcessing) {
    moduleProcessing.value[code] = isProcessing
  }

  // controlCenterPosition
  const centerPositionDisplayed = ref(false)

  const getCenterPositionDisplayed = computed(() => centerPositionDisplayed.value)

  function setCenterPositionDisplayed(pos) {
    centerPositionDisplayed.value = pos
  }

  // isMobile
  const isMobile = ref(window.matchMedia("(max-width: 768px)").matches || false)

  const getIsMobile = computed(() => isMobile.value)

  function setIsMobile(v) {
    isMobile.value = v
  }

  // showDraughtControl
  const displayDraughtControl = ref(false)

  const showDraughtControl = computed(() => isMobile.value ? displayDraughtControl.value : true)

  function setDisplayDraughtControl(v) {
    displayDraughtControl.value = v
  }

  // layer list filter selected
  const layerListFilter = ref(localStorage.getItem('layerListFilter') || 'ENC')

  const getLayerListFilter = computed(() => layerListFilter.value)

  function setLayerListFilter(filter) {
    localStorage.setItem('layerListFilter', filter)
    layerListFilter.value = filter;
  }

  // Ship Placement save collapsed categories
  const collapsedCatPlacement = ref(JSON.parse(localStorage.getItem('collapsedCatPlacement')) || {})

  const getCollapsedCatPlacement = computed(() => collapsedCatPlacement.value)

  function setCollapsedCatPlacement(cat, value) {
    const list = collapsedCatPlacement.value
    list[cat] = value
    localStorage.setItem('collapsedCatPlacement', JSON.stringify(list))
    collapsedCatPlacement.value = list
  }

  // manage graticule active
  const graticuleIsActive = ref(localStorage.getItem('graticuleIsActive') === 'true' || false)
  
  const getGraticuleIsActive = computed(() => graticuleIsActive.value)

  function setGraticuleIsActive(v) {
    localStorage.setItem('graticuleIsActive', v)
    graticuleIsActive.value = v
  }

  // selected ais vessel
  const vesselSelected = ref(null)

  const getVesselSelected = computed(() => vesselSelected.value)

  function setVesselSelected(v) {
    vesselSelected.value = v
  }

  // Ulhysses messages
  const ulhMessages = ref([])
  const currentProgressMsgId = ref(null)

  const getUlhMessages = computed(() => ulhMessages.value)

  function addUlhMessage(message) {
    // simple message
    if (typeof message === 'string' && !message.startsWith('{')) {
      ulhMessages.value.push({
        id: uuidv4(),
        type: 'text',
        msg: message,
        timestamp: Date.now()
      })
      return
    }
    // JSON message
    let parsed
    try {
      parsed = JSON.parse(message)
    } catch {
      // fallback : non parseable message -> display as string
      ulhMessages.value.push({
        id: uuidv4(),
        type: 'text',
        msg: message,
        timestamp: Date.now()
      })
      return
    }

    if ('isWorking' in parsed) {
      if (parsed.isWorking) clearUlhMessage()
      else {
        const lastActive = ulhMessages.value.filter(l => l.type === 'progress' && !l.completed).slice(-1)[0]
        if (lastActive) {
          lastActive.completed = true
          lastActive.progress = 1
        }
        ulhMessages.value.push({
          id: uuidv4(),
          type: 'text',
          msg: 'End of processing',
          timestamp: Date.now()
        })
      }
    } else {
      const { msg, msgid, step } = parsed
      // Complete last sequence if new msgid
      if (currentProgressMsgId.value && currentProgressMsgId.value !== msgid) {
        const lastActive = ulhMessages.value.filter(l => l.type === 'progress' && !l.completed).slice(-1)[0]
        if (lastActive) {
          lastActive.completed = true
          lastActive.progress = 1
        }
      }

      // Update current sequence if same msgid
      const active = ulhMessages.value.filter(l => l.type === 'progress' && l.msgid === msgid && !l.completed).slice(-1)[0]
      if (active) {
        active.progress = Number(step)
        active.msg = msg
        return
      }

      // new sequence
      currentProgressMsgId.value = msgid
      ulhMessages.value.push({
        id: uuidv4(),
        type: 'progress',
        msgid,
        msg,
        progress: Number(step),
        completed: false,
        timestamp: Date.now()
      })
    }
  }

  function clearUlhMessage() {
    ulhMessages.value.length = 0
  }

  return {
    authConfig,
    getAuthConfig,
    setAuthConfig,
    online,
    getOnline,
    setOnline,
    locale,
    getLocale,
    setLocale,
    fontProfile,
    getFontProfile,
    setFontProfile,
    mapDbVersion,
    getMapDbVersion,
    setMapDbVersion,
    visibleOverview,
    getVisibleOverview,
    setVisibleOverview,
    visibleLayers,
    getVisibleLayers,
    setVisibleLayers,
    mapView,
    setMapView,
    getMapView,
    layerExtraParameters,
    getLayerExtraParameters,
    setLayerExtraParameters,
    updateLayerExtraParameters,
    getContours,
    setContours,
    measureUnit,
    getMeasureUnit,
    setMeasureUnit,
    layerColor,
    getLayerColor,
    setLayerColor,
    moduleOpen,
    getModuleOpen,
    setModuleOpen,
    moduleAisIsOpen,
    getModuleAisIsOpen,
    setModuleAisIsOpen,
    moduleReplayIsOpen,
    getModuleReplayIsOpen,
    setModuleReplayIsOpen,
    simplifyGetFeatureInfo,
    getSimplifyGetFeatureInfo,
    setSimplifyGetFeatureInfo,
    featureInfo,
    getFeatureInfo,
    setFeatureInfo,
    editionMode,
    getEditionMode,
    setEditionMode,
    aisData,
    getAisData,
    setAisData,
    replayAisData,
    getReplayAisData,
    setReplayAisData,
    moduleProcessing,
    getModuleProcessing,
    setModuleProcessing,
    centerPositionDisplayed,
    getCenterPositionDisplayed,
    setCenterPositionDisplayed,
    isMobile,
    getIsMobile,
    setIsMobile,
    displayDraughtControl,
    showDraughtControl,
    setDisplayDraughtControl,
    layerListFilter,
    getLayerListFilter,
    setLayerListFilter,
    collapsedCatPlacement,
    getCollapsedCatPlacement,
    setCollapsedCatPlacement,
    graticuleIsActive,
    getGraticuleIsActive,
    setGraticuleIsActive,
    vesselSelected,
    getVesselSelected,
    setVesselSelected,
    ulhMessages,
    getUlhMessages,
    addUlhMessage,
    clearUlhMessage,
    $reset
  }
});
